import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { UserBoat } from '@regatta-replay/types';
import { MomentsFeed } from './MomentsFeed';
import { YourRaces, NoRacesYet } from './YourRaces';
import { RunYourRegattaCTA } from '@/components/marketing/RunYourRegattaCTA';
import type {
  HomeBoat,
  HomeMomentsPage,
  HomeRegatta,
  HomeResponse,
  UserRacesResponse,
} from './home-types';

/**
 * The signed-in home: your boat, the regattas, your moments. Never more than
 * three things on the screen. Signed-out visitors never see this page — the
 * route sends them to the landing page instead.
 *
 * Data comes from GET /me/home in one round trip (backend handoff:
 * docs/HANDOFF-Home-Dashboard.md). Until that endpoint exists the page
 * composes boat + regattas from GET /me and GET /regattas, and the moments
 * module simply doesn't render — ship the UI first, light it up after.
 */

/**
 * Regattas shown before the list defers to /regattas.
 *
 * The moments feed is what this page is for; an uncapped regatta list buries it
 * and leaves the right-hand column stranded half a page up. Four keeps the two
 * columns finishing within a screen of each other.
 */
const HOME_REGATTA_LIMIT = 4;

interface MeResponse {
  user: { name?: string };
  boats: { owned: UserBoat[]; crewing: UserBoat[]; supporting?: UserBoat[] };
}

interface RegattasResponse {
  items: Array<{
    regattaId: string;
    slug: string;
    name: string;
    shortName?: string;
    venue?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  }>;
}

function fmtDates(a?: string, b?: string): string | null {
  if (!a) return null;
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const start = new Date(a + 'T00:00:00');
  if (!b) return start.toLocaleDateString('en-US', opts);
  const end = new Date(b + 'T00:00:00');
  return `${start.toLocaleDateString('en-US', opts)}–${end.toLocaleDateString('en-US', opts)}`;
}

/**
 * The other boats on the account, as a switch under the plate.
 *
 * Only when there IS another boat — a single-boat sailor, which is most of
 * them, sees exactly what they saw before. Sail numbers rather than names
 * because the plate above is already a sail number, so the eye matches on the
 * same token.
 */
function BoatSwitch({
  boats,
  activeId,
  onSelect,
}: {
  boats: HomeBoat[];
  activeId: string;
  onSelect: (boatId: string) => void;
}) {
  if (boats.length < 2) return null;
  return (
    <div className="mt-3 flex w-full flex-wrap items-center gap-1.5" role="group" aria-label="Your boats">
      {boats.map((b) => {
        const active = b.boatId === activeId;
        return (
          <button
            key={b.boatId}
            type="button"
            onClick={() => onSelect(b.boatId)}
            aria-pressed={active}
            title={b.boatName}
            className={`rounded-full border px-3 py-1 font-viewer-mono text-[11px] tracking-[.08em] transition-colors ${
              active
                ? 'border-viewer-accent/50 bg-viewer-accent/15 text-viewer-ink'
                : 'border-viewer-line/30 text-viewer-muted hover:border-viewer-line2/45 hover:text-viewer-ink'
            }`}
          >
            {[b.sailCountry, b.sailNumber].filter(Boolean).join(' ')}
          </button>
        );
      })}
    </div>
  );
}

function SailPlate({
  boat,
  boats,
  activeId,
  onSelect,
}: {
  boat: HomeBoat;
  boats: HomeBoat[];
  activeId: string;
  onSelect: (boatId: string) => void;
}) {
  // Per boat, not per account: someone can own one boat and follow another, and
  // an account-level flag mislabels one of them the moment they switch.
  const their = boat.isSupported === true;
  const watchTarget = boat.latestRaceId ? `/view/${boat.latestRaceId}` : null;
  const roleCls =
    their
      ? 'border-viewer-violet/35 bg-viewer-violet/10 text-viewer-violet'
      : 'border-viewer-good/35 bg-viewer-good/10 text-viewer-good';

  return (
    <div className="relative overflow-hidden border-b border-viewer-line/25 bg-gradient-to-b from-viewer-panel2 to-viewer-deep">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-24 h-80 w-80 rounded-full"
        style={{ background: 'radial-gradient(circle at 35% 35%, rgb(88 180 255 / 0.13), transparent 65%)' }}
      />
      <div className="mx-auto flex max-w-5xl flex-wrap items-end gap-x-6 gap-y-4 px-5 py-6 sm:px-8">
        <Link to={`/boat/${boat.boatId}`} className="group block">
          <span className="block font-viewer-mono text-[12px] font-medium tracking-[.34em] text-viewer-accent">
            {boat.sailCountry ?? ''}
          </span>
          <span className="block font-viewer-mono text-5xl font-bold leading-[.95] tracking-tight text-viewer-ink group-hover:text-white sm:text-6xl">
            {boat.sailNumber}
          </span>
        </Link>
        <div className="min-w-0">
          <Link
            to={`/boat/${boat.boatId}`}
            className="font-viewer-display text-2xl font-bold leading-tight text-viewer-ink hover:underline sm:text-3xl"
          >
            {boat.boatName}
          </Link>
          <span
            className={`ml-2.5 inline-block rounded-full border px-2.5 py-0.5 align-[3px] font-viewer-mono text-[9px] uppercase tracking-[.12em] ${roleCls}`}
          >
            {boat.role}
          </span>
          <p className="mt-0.5 text-[12.5px] text-viewer-muted">
            {their
              ? 'You follow this boat'
              : [boat.classDisplayName, boat.standingLine].filter(Boolean).join(' · ') || 'Claimed to your account'}
          </p>
        </div>
        <div className="flex w-full gap-2.5 sm:ml-auto sm:w-auto">
          <Link
            to="/profile"
            className="rounded-lg border border-viewer-line2/40 px-4 py-2.5 text-[13px] font-semibold text-viewer-ink hover:bg-viewer-panel2"
          >
            My boats
          </Link>
          {watchTarget && (
            <Link
              to={watchTarget}
              className="flex-1 rounded-lg bg-brand-ink px-4 py-2.5 text-center text-[13px] font-semibold text-white hover:bg-brand-dark sm:flex-none"
            >
              {their ? 'Watch their latest race' : 'Watch your latest race'}
            </Link>
          )}
        </div>
        <BoatSwitch boats={boats} activeId={activeId} onSelect={onSelect} />
      </div>
    </div>
  );
}

function ClaimPrompt() {
  return (
    <div className="border-b border-viewer-line/25 bg-gradient-to-b from-viewer-panel2 to-viewer-deep">
      <div className="mx-auto max-w-5xl px-5 py-8 text-center sm:px-8">
        <h1 className="font-viewer-display text-[22px] font-bold text-viewer-ink">Find your boat</h1>
        <p className="mx-auto mt-1 max-w-[30ch] text-[13px] text-viewer-muted">
          Claim it once and this page becomes yours — your races, your moments.
        </p>
        <Link
          to="/onboarding"
          className="mt-4 inline-block rounded-lg bg-brand-ink px-6 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-dark"
        >
          Find my boat
        </Link>
        <p className="mt-2 text-[11px] text-viewer-faint">or keep watching as a guest</p>
      </div>
    </div>
  );
}

/**
 * The invitation, as a panel in the right-hand rail.
 *
 * It used to be a full-width band pinned under both columns, which made it the
 * widest and brightest thing on a page whose job is the regattas — and it was
 * the THIRD copy of the same action, after the nav button and the empty-state
 * card in `NoRacesYet`. As a rail panel it keeps the one blue button in the
 * body without outranking the list beside it.
 */
function CreateRacePanel() {
  return (
    <div className="mt-5 rounded-xl border border-viewer-line/25 bg-gradient-to-b from-viewer-panel2 to-viewer-panel px-4 py-4">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-viewer-accent/30 bg-viewer-accent/10 text-[14px] text-viewer-accent">
        ↑
      </span>
      <p className="mt-3 font-viewer-display text-[15px] font-bold text-viewer-ink">
        Create your own race
      </p>
      <p className="mt-1 text-[11.5px] leading-relaxed text-viewer-muted">
        Send us your tracks — we build it and email you the link.
      </p>
      <Link
        to="/create-race"
        className="mt-3.5 block rounded-lg bg-brand-ink px-4 py-2.5 text-center text-[12.5px] font-semibold text-white hover:bg-brand-dark"
      >
        Upload tracks
      </Link>
    </div>
  );
}

function RegattaCard({ r }: { r: HomeRegatta }) {
  const dates = fmtDates(r.startDate, r.endDate);
  const finished = r.status === 'completed';
  return (
    <Link
      to={`/regatta/${r.slug}`}
      className="mb-3 block rounded-xl border border-viewer-line/25 bg-viewer-panel px-4 py-3.5 transition-colors hover:border-viewer-line2/40"
    >
      <div className="flex items-center gap-2.5">
        <span className="min-w-0 truncate font-viewer-display text-[18px] font-bold text-viewer-ink">
          {r.name}
        </span>
        {finished && (
          <span className="ml-auto shrink-0 rounded-full border border-viewer-good/30 bg-viewer-good/10 px-2 py-0.5 font-viewer-mono text-[8.5px] uppercase tracking-[.11em] text-viewer-good">
            Results final
          </span>
        )}
      </div>
      <p className="mt-0.5 text-[12px] text-viewer-muted">
        {[r.venue, dates].filter(Boolean).join(' · ')}
      </p>
      {(r.raceCount != null || r.yourBoatRacedHere) && (
        <div className="mt-2.5 flex items-end gap-4 border-t border-viewer-line/25 pt-2.5">
          {r.raceCount != null && (
            <span className="text-[10.5px] text-viewer-faint">
              <b className="block font-viewer-mono text-[14px] font-medium text-viewer-ink">{r.raceCount}</b>
              races
            </span>
          )}
          {r.entryCount != null && (
            <span className="text-[10.5px] text-viewer-faint">
              <b className="block font-viewer-mono text-[14px] font-medium text-viewer-ink">{r.entryCount}</b>
              boats
            </span>
          )}
          {r.replayCount != null && (
            <span className="text-[10.5px] text-viewer-faint">
              <b className="block font-viewer-mono text-[14px] font-medium text-viewer-ink">{r.replayCount}</b>
              replays
            </span>
          )}
          {r.yourBoatRacedHere && (
            <span className="ml-auto text-[11px] font-semibold text-viewer-accent">Your boat raced here →</span>
          )}
        </div>
      )}
    </Link>
  );
}

/** Compose a HomeResponse from the endpoints that exist today. */
async function composeFallback(): Promise<HomeResponse> {
  const [me, regattas] = await Promise.all([
    api.get<MeResponse>('/me'),
    api.get<RegattasResponse>('/regattas', { silent: true }),
  ]);
  const first =
    me.boats.owned[0] ?? me.boats.crewing[0] ?? me.boats.supporting?.[0] ?? null;
  const supported = !!first && !me.boats.owned.length && !me.boats.crewing.length;

  return {
    boat: first
      ? {
          boatId: first.boatId,
          sailCountry: first.sailCountry,
          sailNumber: first.sailNumber,
          boatName: first.boatName,
          classDisplayName: first.classDisplayName || undefined,
          role: me.boats.owned[0] ? 'owner' : me.boats.crewing[0] ? 'crew' : 'supporter',
        }
      : null,
    boatIsSupported: supported,
    regattas: (regattas.items ?? []).map((r) => ({
      regattaId: r.regattaId,
      slug: r.slug,
      name: r.name,
      shortName: r.shortName,
      venue: r.venue,
      startDate: r.startDate,
      endDate: r.endDate,
      status: r.status,
    })),
    moments: null,
  };
}

/** Remembers which boat you were last looking at, per account. */
const ACTIVE_BOAT_KEY = 'rr:home:activeBoat';

export function HomePage() {
  const { user } = useAuthStore();
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [races, setRaces] = useState<UserRacesResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [activeBoatId, setActiveBoatId] = useState<string | null>(null);
  const [moments, setMoments] = useState<HomeMomentsPage | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // The aggregate endpoint, once the backend lands it…
        const res = await api.get<HomeResponse>('/me/home', { silent: true });
        if (live) setHome(res);
      } catch {
        // …and the composed fallback until then.
        try {
          const res = await composeFallback();
          if (live) setHome(res);
        } catch {
          if (live) setFailed(true);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [user?.userId]);

  // Separate call so a missing races endpoint can't blank the whole dashboard.
  useEffect(() => {
    let live = true;
    api
      .get<UserRacesResponse>('/me/races', { silent: true })
      .then((res) => live && setRaces(res))
      .catch(() => live && setRaces({ items: [], total: 0 }));
    return () => {
      live = false;
    };
  }, [user?.userId]);

  /** Primary first, and a single-boat response still yields a one-item list. */
  const boats: HomeBoat[] = home?.boats ?? (home?.boat ? [home.boat] : []);

  /**
   * Settle on a boat once the account loads: the one they last looked at if it
   * is still claimed, otherwise the primary. Reading storage here rather than
   * in `useState`'s initialiser because the claimed set isn't known until the
   * response arrives, and restoring a boat they no longer have would show an
   * empty plate.
   */
  useEffect(() => {
    if (boats.length === 0) return;
    const remembered = (() => {
      try {
        return window.localStorage?.getItem(ACTIVE_BOAT_KEY);
      } catch {
        return null;
      }
    })();
    const valid = boats.some((b) => b.boatId === remembered);
    setActiveBoatId((current) =>
      current && boats.some((b) => b.boatId === current)
        ? current
        : valid
          ? remembered
          : boats[0].boatId,
    );
    // Keyed on the claimed set, so a boat added or removed elsewhere re-settles.
  }, [boats.map((b) => b.boatId).join(','), boats.length]);

  const activeBoat = boats.find((b) => b.boatId === activeBoatId) ?? boats[0] ?? null;

  /** The inlined first page belongs to ALL boats; adopt it until a boat is chosen. */
  useEffect(() => {
    setMoments(home?.moments ?? null);
  }, [home]);

  /**
   * Switching the plate switches the feed with it.
   *
   * Skipped entirely for a single-boat account: the page already arrived with
   * that boat's moments inlined, and refetching them would be a second request
   * for the same answer.
   */
  useEffect(() => {
    if (!activeBoatId || boats.length < 2) return;
    let live = true;
    api
      .get<HomeMomentsPage>(
        `/me/moments?page=1&pageSize=10&boatId=${encodeURIComponent(activeBoatId)}`,
        { silent: true },
      )
      .then((res) => live && setMoments(res.total > 0 ? res : null))
      // Keep whatever is on screen: an empty feed reads as "this boat has no
      // moments", which is a stronger claim than a failed request supports.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [activeBoatId, boats.length]);

  const selectBoat = (boatId: string) => {
    setActiveBoatId(boatId);
    try {
      window.localStorage?.setItem(ACTIVE_BOAT_KEY, boatId);
    } catch {
      // Storage unavailable: the choice still holds for this session.
    }
  };

  const fetchMoments = async (page: number): Promise<HomeMomentsPage> => {
    const scope = boats.length > 1 && activeBoatId ? `&boatId=${encodeURIComponent(activeBoatId)}` : '';
    return api.get<HomeMomentsPage>(`/me/moments?page=${page}&pageSize=10${scope}`, { silent: true });
  };

  return (
    <div className="min-h-[70vh] bg-viewer-deep">
      <Helmet>
        <title>Home - RegattaReplay™</title>
      </Helmet>

      {!home && !failed && (
        <div className="mx-auto max-w-5xl px-5 py-16 text-center text-sm text-viewer-faint">Loading…</div>
      )}

      {failed && (
        <div className="mx-auto max-w-5xl px-5 py-16 text-center">
          <p className="text-sm text-viewer-muted">Couldn’t load your home page.</p>
          <Link to="/regattas" className="mt-2 inline-block text-sm font-semibold text-viewer-accent hover:underline">
            Browse regattas instead
          </Link>
        </div>
      )}

      {home && (
        <>
          {activeBoat ? (
            <SailPlate
              boat={activeBoat}
              boats={boats}
              activeId={activeBoat.boatId}
              onSelect={selectBoat}
            />
          ) : (
            <ClaimPrompt />
          )}

          {/* Wide column = what you watch, narrow column = what you manage. */}
          <div className="mx-auto grid max-w-5xl gap-8 px-5 py-7 sm:px-8 lg:grid-cols-[7fr_5fr]">
            <div>
              {moments && moments.total > 0 && (
                <div className="mb-7">
                  {/* Keyed on the boat: MomentsFeed seeds its list and page
                      counter from `initial` ONCE, so without a remount a switch
                      would leave the previous boat's moments on screen and page
                      2 of the new boat appended under them. */}
                  <MomentsFeed
                    key={activeBoat?.boatId ?? 'all'}
                    initial={moments}
                    their={activeBoat?.isSupported === true}
                    fetchPage={fetchMoments}
                  />
                </div>
              )}

              <div className="mb-3 flex items-baseline gap-2.5">
                <h2 className="font-viewer-display text-[14px] font-bold uppercase tracking-[.13em] text-viewer-ink">
                  Regattas
                </h2>
                <span className="text-[11px] text-viewer-faint">{home.regattas.length}</span>
                {home.regattas.length > HOME_REGATTA_LIMIT && (
                  <Link
                    to="/regattas"
                    className="ml-auto text-[11.5px] font-semibold text-viewer-accent hover:underline"
                  >
                    See all {home.regattas.length}
                  </Link>
                )}
              </div>
              {home.regattas.slice(0, HOME_REGATTA_LIMIT).map((r) => (
                <RegattaCard key={r.regattaId} r={r} />
              ))}
              {home.regattas.length > HOME_REGATTA_LIMIT ? (
                // Say what was left out. A silently truncated list reads as the
                // whole list, and then a sailor thinks their regatta is missing.
                <p className="mt-1 text-[10.5px] text-viewer-faint">
                  {home.regattas.length - HOME_REGATTA_LIMIT} more ·{' '}
                  <Link to="/regattas" className="font-semibold text-viewer-accent hover:underline">
                    See all {home.regattas.length}
                  </Link>
                </p>
              ) : (
                <p className="mt-1 text-[10.5px] text-viewer-faint">
                  Every event we cover appears here on publish.
                </p>
              )}
            </div>

            <div>
              {races && races.items.length > 0 ? (
                <>
                  <YourRaces races={races.items} limit={4} />
                  <CreateRacePanel />
                </>
              ) : (
                <>
                  <div className="mb-3 flex items-baseline gap-2.5">
                    <h2 className="font-viewer-display text-[14px] font-bold uppercase tracking-[.13em] text-viewer-ink">
                      Your races
                    </h2>
                  </div>
                  {/* `NoRacesYet` already makes this exact offer, so the panel
                      would be the same invitation twice in one column. */}
                  <NoRacesYet />
                </>
              )}
              {/* Outside the branch on purpose: the offer to run someone's event
                  is aimed at an organising authority, and whether THIS visitor
                  happens to have races of their own says nothing about that. */}
              <RunYourRegattaCTA className="mt-5" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
